import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Maximize2, Volume2, VolumeX, Loader2, TimerReset, Flag, MessageCircleQuestion, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, apiError } from "../lib/api";

export const PHASE_LABELS = {
  not_started: "Sin iniciar",
  preparation: "Preparación",
  presentation: "Exposición",
  qa: "Preguntas y respuestas",
  completed: "Taller finalizado",
};

const fmt = (secs) => {
  const neg = secs < 0;
  const v = Math.abs(Math.floor(secs));
  const h = Math.floor(v / 3600);
  const m = Math.floor((v % 3600) / 60);
  const s = v % 60;
  const base = h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return neg ? `+${base}` : base;
};

export const TimerPanel = ({ workshopId, role, readOnly = false, projection = false, onActionDone }) => {
  const [data, setData] = useState(null);
  const [syncError, setSyncError] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [acting, setActing] = useState(false);
  const [muted, setMuted] = useState(localStorage.getItem("hz_timer_muted") === "1");
  const [flash, setFlash] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState("");
  const [adjustMin, setAdjustMin] = useState(5);
  const [adjustSign, setAdjustSign] = useState(1);
  const offsetRef = useRef(0);
  const firedRef = useRef(new Set());
  const audioRef = useRef(null);
  const mutedRef = useRef(muted);
  const prevStatusRef = useRef(null);
  const boxRef = useRef(null);
  const [, setTick] = useState(0);

  mutedRef.current = muted;

  const beep = (freq = 880, count = 1) => {
    if (mutedRef.current) return;
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      for (let i = 0; i < count; i++) {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = freq;
        o.type = "sine";
        o.connect(g);
        g.connect(ctx.destination);
        const t0 = ctx.currentTime + i * 0.35;
        g.gain.setValueAtTime(0.001, t0);
        g.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.3);
        o.start(t0);
        o.stop(t0 + 0.32);
      }
    } catch (e) {}
  };

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/timer/${workshopId}`);
      offsetRef.current = Date.parse(d.server_time) - Date.now();
      const st = d.session.status;
      if (prevStatusRef.current && prevStatusRef.current !== st) {
        firedRef.current = new Set();
        if (st === "qa") { setFlash("Preguntas y respuestas"); beep(990, 2); }
        if (st === "completed") { setFlash("Taller finalizado"); beep(1200, 3); }
        if (st === "qa" || st === "completed") setTimeout(() => setFlash(null), 4500);
      }
      prevStatusRef.current = st;
      setData(d);
      setSyncError(false);
      setLastSync(new Date());
    } catch (e) {
      setSyncError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000); // sincronización periódica con el servidor (polling)
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000); // solo visualización; la fuente de verdad es el servidor
    return () => clearInterval(t);
  }, []);

  const s = data?.session;
  const nowServer = Date.now() + offsetRef.current;
  let elapsed = 0;
  if (s?.phase_started_at && s.status !== "not_started") {
    const ref = s.is_paused && s.paused_at ? Date.parse(s.paused_at) : nowServer;
    elapsed = Math.max(0, (ref - Date.parse(s.phase_started_at)) / 1000);
  }
  const running = s && ["preparation", "presentation", "qa"].includes(s.status);
  const remaining = s?.phase_duration_seconds != null && running ? s.phase_duration_seconds - elapsed : null;
  let totalElapsed = 0;
  if (s?.presentation_started_at) {
    const end = s.completed_at ? Date.parse(s.completed_at) : s.is_paused && s.paused_at ? Date.parse(s.paused_at) : nowServer;
    totalElapsed = Math.max(0, (end - Date.parse(s.presentation_started_at)) / 1000);
  }

  useEffect(() => {
    if (remaining == null || s?.is_paused) return;
    const fire = (key, freq, count) => {
      if (!firedRef.current.has(key)) {
        firedRef.current.add(key);
        beep(freq, count);
      }
    };
    if (remaining <= 60 && remaining > 0) fire(`m1-${s.status}`, 1200, 3);
    else if (remaining <= 300) fire(`m5-${s.status}`, 990, 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Math.floor(remaining ?? -1)]);

  const act = async (action, extra = {}) => {
    if (acting) return;
    setActing(true);
    try {
      const { data: d } = await api.post(`/timer/${workshopId}/action`, { action, ...extra });
      offsetRef.current = Date.parse(d.server_time) - Date.now();
      setData(d);
      setSyncError(false);
      setDialog(null);
      setReason("");
      onActionDone?.();
    } catch (err) {
      toast.error(apiError(err, "No se pudo aplicar la acción. Verifica tu conexión."));
    } finally {
      setActing(false);
    }
  };

  if (!s) {
    return <div className="flex items-center justify-center gap-2 py-16 text-hz-navy" data-testid="timer-loading"><Loader2 className="h-5 w-5 animate-spin" /> Cargando temporizador…</div>;
  }

  let tone = "text-hz-cyan";
  let toneBg = "border-hz-cyan/40 bg-hz-cyan/5";
  if (s.status === "completed") { tone = "text-hz-success"; toneBg = "border-hz-success/40 bg-hz-success/5"; }
  else if (s.is_paused || s.status === "not_started") { tone = "text-hz-ink/50"; toneBg = "border-hz-gray/50 bg-hz-gray/10"; }
  else if (remaining != null && remaining <= 60) { tone = "text-hz-error"; toneBg = "border-hz-error/50 bg-hz-error/5"; }
  else if (remaining != null && remaining <= 300) { tone = "text-hz-warning"; toneBg = "border-hz-warning/50 bg-hz-warning/5"; }
  else if (remaining != null && remaining <= 600) { tone = "text-hz-warning"; toneBg = "border-hz-warning/40 bg-hz-warning/5"; }

  const display = remaining != null ? remaining : elapsed;
  const canOperate = !readOnly && ["admin", "welcome_coordinator", "room_manager"].includes(role);
  const canAdjust = !readOnly && ["admin", "welcome_coordinator"].includes(role);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else boxRef.current?.requestFullscreen?.();
  };

  return (
    <div ref={boxRef} className={`relative rounded-2xl border-2 p-6 ${toneBg} ${projection ? "flex min-h-screen flex-col justify-center" : ""}`} data-testid="timer-panel">
      {flash && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-hz-navy/95" data-testid="timer-flash">
          <p className="animate-fade-up px-6 text-center text-3xl font-extrabold text-white sm:text-5xl">{flash}</p>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className={`font-bold uppercase tracking-widest ${projection ? "text-lg" : "text-xs"} text-hz-blue`}>{data.workshop.name}</p>
          <p className={`mt-1 font-extrabold ${tone} ${projection ? "text-3xl" : "text-lg"}`} data-testid="timer-phase">{PHASE_LABELS[s.status]}{s.is_paused ? " (en pausa)" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { const v = !muted; setMuted(v); localStorage.setItem("hz_timer_muted", v ? "1" : "0"); }} data-testid="timer-mute-btn" aria-label={muted ? "Activar sonido" : "Silenciar"} className="rounded-full border border-hz-gray/50 bg-white p-2 text-hz-navy">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <button onClick={toggleFullscreen} data-testid="timer-fullscreen-btn" aria-label="Pantalla completa" className="rounded-full border border-hz-gray/50 bg-white p-2 text-hz-navy">
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="py-6 text-center">
        <p className={`font-extrabold tabular-nums tracking-tight ${tone} ${projection ? "text-[9rem] leading-none sm:text-[13rem]" : "text-7xl sm:text-8xl"}`} data-testid="timer-display">
          {fmt(display)}
        </p>
        {remaining != null && remaining < 0 && (
          <p className="mt-1 text-sm font-bold text-hz-error" data-testid="timer-overtime">Tiempo agotado</p>
        )}
        {remaining == null && running && <p className="mt-1 text-xs font-semibold text-hz-ink/50">Tiempo transcurrido de la fase</p>}
      </div>

      <div className={`grid gap-3 ${projection ? "grid-cols-2 text-lg" : "grid-cols-2 text-xs"} sm:grid-cols-3`}>
        <div className="rounded-xl bg-white/70 px-3 py-2">
          <p className="font-semibold text-hz-ink/50">Tiempo total</p>
          <p className="font-extrabold tabular-nums text-hz-navy" data-testid="timer-total">{fmt(totalElapsed)}</p>
        </div>
        <div className="rounded-xl bg-white/70 px-3 py-2">
          <p className="font-semibold text-hz-ink/50">Próxima fase</p>
          <p className="font-bold text-hz-navy" data-testid="timer-next-phase">{s.next_phase || "—"}</p>
        </div>
        <div className="rounded-xl bg-white/70 px-3 py-2">
          <p className="font-semibold text-hz-ink/50">Última sincronización</p>
          <p className="font-bold text-hz-navy" data-testid="timer-last-sync">{lastSync ? lastSync.toLocaleTimeString("es-MX") : "—"}</p>
        </div>
      </div>

      {syncError && (
        <p className="mt-3 flex items-center gap-2 rounded-lg border border-hz-error/40 bg-hz-error/5 px-3 py-2 text-xs font-semibold text-hz-error" data-testid="timer-sync-error" role="alert">
          <WifiOff className="h-4 w-4" /> Sin conexión con el servidor. El tiempo mostrado puede no estar actualizado; los cambios no se guardarán hasta recuperar conexión.
        </p>
      )}

      {canOperate && (
        <div className="mt-5 flex flex-wrap gap-2 print:hidden">
          {s.status === "not_started" && (
            <>
              <Button onClick={() => act("start_preparation")} disabled={acting} data-testid="timer-start-prep-btn" variant="outline" className="font-bold text-hz-navy">Iniciar preparación</Button>
              <Button onClick={() => act("start_presentation")} disabled={acting} data-testid="timer-start-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue"><Play className="mr-1 h-4 w-4" /> Iniciar exposición</Button>
            </>
          )}
          {s.status === "preparation" && !s.is_paused && (
            <Button onClick={() => act("start_presentation")} disabled={acting} data-testid="timer-start-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue"><Play className="mr-1 h-4 w-4" /> Iniciar exposición</Button>
          )}
          {running && !s.is_paused && (
            <Button onClick={() => act("pause")} disabled={acting} variant="outline" data-testid="timer-pause-btn" className="font-bold text-hz-navy"><Pause className="mr-1 h-4 w-4" /> Pausar</Button>
          )}
          {s.is_paused && (
            <Button onClick={() => act("resume")} disabled={acting} data-testid="timer-resume-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue"><Play className="mr-1 h-4 w-4" /> Reanudar</Button>
          )}
          {s.status === "presentation" && (
            <Button onClick={() => act("to_qa")} disabled={acting} variant="outline" data-testid="timer-qa-btn" className="font-bold text-hz-navy"><MessageCircleQuestion className="mr-1 h-4 w-4" /> Pasar a Q&A</Button>
          )}
          {running && (
            <Button onClick={() => act("complete")} disabled={acting} variant="outline" data-testid="timer-complete-btn" className="border-hz-success/50 font-bold text-hz-success"><Flag className="mr-1 h-4 w-4" /> Finalizar taller</Button>
          )}
          {canAdjust && running && (
            <Button onClick={() => setDialog("adjust")} variant="outline" data-testid="timer-adjust-btn" className="font-bold text-hz-warning"><TimerReset className="mr-1 h-4 w-4" /> Ajustar tiempo</Button>
          )}
          {role === "admin" && s.status !== "not_started" && (
            <Button onClick={() => setDialog("reset")} variant="outline" data-testid="timer-reset-btn" className="border-hz-error/40 font-bold text-hz-error"><RotateCcw className="mr-1 h-4 w-4" /> Reiniciar</Button>
          )}
        </div>
      )}

      <Dialog open={dialog === "adjust"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-bold text-hz-navy">Ajustar tiempo de la fase</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Minutos</Label>
                <Input type="number" min="1" value={adjustMin} onChange={(e) => setAdjustMin(Number(e.target.value) || 0)} data-testid="timer-adjust-minutes" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Acción</Label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <button onClick={() => setAdjustSign(1)} data-testid="timer-adjust-add" className={`rounded-md border-2 px-2 py-2 text-xs font-bold ${adjustSign === 1 ? "border-hz-cyan bg-hz-cyan/10 text-hz-navy" : "border-hz-gray/40 text-hz-ink/60"}`}>Agregar</button>
                  <button onClick={() => setAdjustSign(-1)} data-testid="timer-adjust-sub" className={`rounded-md border-2 px-2 py-2 text-xs font-bold ${adjustSign === -1 ? "border-hz-cyan bg-hz-cyan/10 text-hz-navy" : "border-hz-gray/40 text-hz-ink/60"}`}>Quitar</button>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Motivo * (queda en bitácora)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="timer-adjust-reason" className="mt-1" placeholder="Ej. El ponente inició 5 minutos tarde" />
            </div>
            <Button onClick={() => act("adjust", { add_seconds: adjustSign * adjustMin * 60, reason })} disabled={acting || reason.trim().length < 5} data-testid="timer-adjust-confirm" className="w-full bg-hz-navy font-bold text-white">Aplicar ajuste</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "reset"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-bold text-hz-error">Reiniciar temporizador</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-hz-ink/70">Esta acción regresa el taller a “Sin iniciar” y queda registrada en la bitácora. Solo administradores.</p>
            <div>
              <Label className="text-xs font-semibold">Motivo * </Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="timer-reset-reason" className="mt-1" placeholder="Ej. Error de operación, se reinicia la sesión" />
            </div>
            <Button onClick={() => act("reset", { reason })} disabled={acting || reason.trim().length < 5} data-testid="timer-reset-confirm" className="w-full bg-hz-error font-bold text-white">Confirmar reinicio</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
